// Average bytes per record based on typical customer data
const BYTES_PER_RECORD = {
  CSV: 175, // Typical CSV row with customer data
  PDF: 450, // PDF with formatting and layout
};

// Fixed overhead for file headers and structure
const FILE_OVERHEAD = {
  CSV: 500, // CSV headers
  PDF: 50000, // PDF structure, fonts, formatting
};

/**
 * Estimate the file size for an export
 * @param {string} format - Export format ('CSV' or 'PDF')
 * @param {number} recordCount - Number of records to export
 * @returns {number} Estimated file size in bytes
 */
export const estimateExportSize = (format, recordCount) => {
  const bytesPerRecord = BYTES_PER_RECORD[format] || BYTES_PER_RECORD.CSV;
  const overhead = FILE_OVERHEAD[format] || FILE_OVERHEAD.CSV;

  return recordCount * bytesPerRecord + overhead;
};

/**
 * Format bytes into human-readable file size
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size string
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB'];
  const base = 1024;
  const digitGroups = Math.floor(Math.log(bytes) / Math.log(base));

  if (digitGroups === 0) {
    return `${bytes} B`;
  }

  const value = bytes / Math.pow(base, digitGroups);
  const formatted = value.toFixed(1);

  // Remove .0 for whole numbers
  const cleanFormatted = formatted.endsWith('.0')
    ? formatted.slice(0, -2)
    : formatted;

  return `${cleanFormatted} ${units[digitGroups]}`;
};

/**
 * Get formatted file size estimate for export
 * @param {string} format - Export format ('CSV' or 'PDF')
 * @param {number} recordCount - Number of records to export
 * @returns {string} Formatted file size string with ~ prefix
 */
export const getFormattedExportSize = (format, recordCount) => {
  const bytes = estimateExportSize(format, recordCount);
  return `~${formatFileSize(bytes)}`;
};
